import React from "react";
import { Box, Chip, Skeleton, Typography } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ReplayIcon from "@mui/icons-material/Replay";

// Shared by the card and its skeleton so the ghost card has the exact same
// footprint — the same reason the desktop skeletons reuse `classes.cardContainer`.
//
// The cards sit inside the white "My Assessments" panel, so a soft 12px radius on
// a hairline border made them read as one block. Squarer corners + a firmer
// border + their own shadow separate one card from the next.
const CARD_SX = {
  border: "1px solid",
  borderColor: "neutral.clr-200",
  borderRadius: 1.5,
  p: 3,
  bgcolor: "shades.clr-white-900",
  boxShadow: "0px 1px 3px 0px #00000014",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

/** Loading placeholder for <AssessmentCard/>, mirroring its tags/title/meta. */
export function AssessmentCardSkeleton() {
  return (
    <Box sx={CARD_SX}>
      {/* Tag row (New / expiry / attempts allowed) */}
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Skeleton variant="rectangular" width={48} height={22} sx={{ borderRadius: 2 }} />
        <Skeleton variant="rectangular" width={104} height={22} sx={{ borderRadius: 2 }} />
        <Skeleton variant="rectangular" width={88} height={22} sx={{ borderRadius: 2 }} />
      </Box>

      {/* Type */}
      <Skeleton variant="rectangular" width="24%" height={14} />

      {/* Title */}
      <Skeleton variant="rectangular" width="70%" height={20} />

      {/* Meta row (questions / time / attempts) */}
      <Box sx={{ display: "flex", columnGap: 2, mt: 0.5 }}>
        <Skeleton variant="rectangular" width={76} height={14} />
        <Skeleton variant="rectangular" width={60} height={14} />
        <Skeleton variant="rectangular" width={84} height={14} />
      </Box>
    </Box>
  );
}

function MetaItem({ icon: Icon, label }) {
  if (!label) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Icon sx={{ fontSize: 15, color: "neutral.clr-400" }} />
      <Typography variant="body02-regular" color="neutral.clr-600">
        {label}
      </Typography>
    </Box>
  );
}

/** A single assessment card in the "My Assessments" list. */
export default function AssessmentCard({ assessment, onClick }) {
  const {
    isNew,
    expiry,
    attemptsAllowed,
    typeLabel,
    title,
    questionsLabel,
    timeLabel,
    attemptsLabel,
  } = assessment;

  return (
    <Box
      onClick={onClick}
      sx={{
        ...CARD_SX,
        cursor: onClick ? "pointer" : "default",
        transition: "border-color 0.15s, box-shadow 0.15s",
        // Touch has no hover — give the tap itself the feedback instead.
        "&:active": onClick
          ? {
              borderColor: "primary.clr-300",
              boxShadow: "0px 2px 6px 0px #0000001F",
            }
          : undefined,
      }}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        {isNew && (
          <Chip
            label="New"
            size="small"
            sx={{
              bgcolor: "primary.clr-300",
              color: "shades.clr-white-900",
              height: 22,
              "& .MuiChip-label": { px: 1.5, fontSize: 11, fontWeight: 700 },
            }}
          />
        )}
        {expiry && (
          <Chip
            label={expiry}
            size="small"
            variant="outlined"
            sx={{
              height: 22,
              borderColor: "neutral.clr-200",
              color: "neutral.clr-600",
              "& .MuiChip-label": { px: 1.5, fontSize: 11 },
            }}
          />
        )}
        {attemptsAllowed && (
          <Chip
            label={attemptsAllowed}
            size="small"
            variant="outlined"
            sx={{
              height: 22,
              borderColor: "neutral.clr-200",
              color: "neutral.clr-600",
              "& .MuiChip-label": { px: 1.5, fontSize: 11 },
            }}
          />
        )}
      </Box>

      {typeLabel && (
        <Typography
          variant="body02-regular"
          color="neutral.clr-400"
          sx={{ textTransform: "capitalize" }}
        >
          {typeLabel}
        </Typography>
      )}

      <Typography
        variant="body01-semiBold"
        color="neutral.clr-800"
        sx={{ textTransform: "capitalize" }}
      >
        {title}
      </Typography>

      {/* Tight column gap so "Nx Attempted" stays on the same line as the other
          two at 375px — it wrapped at gap: 3, making the card a row taller. Still
          allowed to wrap on anything narrower rather than clipping. */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          columnGap: 2,
          rowGap: 1,
          mt: 0.5,
        }}
      >
        <MetaItem icon={DescriptionOutlinedIcon} label={questionsLabel} />
        <MetaItem icon={AccessTimeIcon} label={timeLabel} />
        <MetaItem icon={ReplayIcon} label={attemptsLabel} />
      </Box>
    </Box>
  );
}
